import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useGetList } from "react-admin";
import { Notification } from "@/interface/entityInterface";
import axiosInstance from "@/utils/axiosInstance";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useUserContext } from "@/context/UserContext";

export const title = "Notifications Popover";

const NotificationsPopover = () => {
  const {decodedToken} = useUserContext()
  const navigate = useNavigate();
  const { data: notifications = [] } = useGetList<Notification>(
    "notifications",
    {
      pagination: { page: 1, perPage: 20 },
      filter: {
        toggleSwitch: true,
      },
    },
  );
  const [unread, setUnread] = useState(0);

  const getUnreadCount = async () => {
    if (!decodedToken?.settings.id) return;
    const { data } = await axiosInstance.get("notifications/unread/count");

    setUnread(data);
  };

  useEffect(() => {
    getUnreadCount();

    const unreadInterval = setInterval(() => getUnreadCount(), 10000);
    return () => {
      clearInterval(unreadInterval);
    };
  }, [decodedToken?.settings.id]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="relative" size="icon" variant="outline">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="-right-2 -top-1 absolute h-4 w-6 items-center justify-center rounded-full p-0 text-xs"
            >
              {unread > 100 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent pos  className="w-80 max-h-80 overflow-y-auto">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Notifications</h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/notifications")}
            >
              View all
            </Button>
          </div>
          <Separator />
          <div className="space-y-2">
            {notifications.length > 0 &&
              notifications.map((notification) => (
                <div key={notification.id} className="w-full">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">{notification.title}</p>
                    <span className="text-gray-500 text-xs">{format(notification.createdAt, "dd MMM yyyy HH:mm")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {notification.message}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsPopover;
